import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import PagePlaceholder from "~/assets/page_placeholder.svg";
import { PageItem } from "~/features/chat/hooks/useDocList";
import { cn } from "~/lib/utils";
import { useSkyViewStore } from "../../../../store";
import { useTextBoundingBoxes } from "../../hooks/useTextBoundingBoxes";
import type { TextSnipState } from "../../hooks/useTextSnip";
import { TextSnipOverlay } from "../TextSnipOverlay";
import { TextHighlightOverlay } from "./TextHighlightOverlay";
import { TruncatedSectionTitle } from "./TruncatedSectionTitle";
import { useGetUrls } from "./useGetUrls";

type DocViewerItemProps = {
  page: PageItem;
  isSelected: boolean;
  isCurrent: boolean;
  togglePageSelection: (pageId: string) => void;
  canLoad: boolean;
  isZoomActive?: boolean;
  organizationId?: string;
  projectId?: string;
  snipState?: TextSnipState;
  onSnipMouseDown?: (
    e: React.MouseEvent,
    containerRect: DOMRect,
    imgRect: DOMRect
  ) => void;
  onSnipMouseMove?: (e: React.MouseEvent, containerRect: DOMRect) => void;
  onSnipMouseUp?: () => void;
};

const DocViewerItemComponent: FC<DocViewerItemProps> = ({
  page,
  isSelected,
  isCurrent,
  togglePageSelection,
  canLoad,
  isZoomActive = false,
  organizationId,
  projectId,
  snipState,
  onSnipMouseDown,
  onSnipMouseMove,
  onSnipMouseUp,
}) => {
  const {
    pageId,
    getUrls,
    opensSection,
    rotate,
    width: originalWidth,
    height: originalHeight,
  } = page;

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isSnipDraggingRef = useRef(false);
  const didSnipDragRef = useRef(false);
  const snipCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      snipCleanupRef.current?.();
    };
  }, []);
  const [hasRetinaError, setHasRetinaError] = useState(false);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [rawZoomOffset, setRawZoomOffset] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);

  const { data: pageUrlsRotate, isFetching } = useGetUrls(
    pageId,
    getUrls,
    canLoad
  );

  const searchQuery = useSkyViewStore((state) => state.searchQuery);
  const highlightedPageId = useSkyViewStore((state) => state.highlightedPageId);

  const shouldFetchBoundingBoxes =
    !!searchQuery && highlightedPageId === pageId;

  const { data: boundingBoxesData } = useTextBoundingBoxes(
    organizationId,
    projectId,
    shouldFetchBoundingBoxes ? pageId : undefined,
    shouldFetchBoundingBoxes ? searchQuery : null
  );

  const rotation = pageUrlsRotate?.rotate ?? 0;

  let ratio = originalWidth / originalHeight;
  if (rotate % 180 === 0) {
    ratio = 1 / ratio;
  }
  const scale = rotation % 180 === 0 ? 1 : ratio;

  const getImageUrl = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return PagePlaceholder;

    const isRetinaDisplay = window.devicePixelRatio > 1;

    if (isZoomActive || isRetinaDisplay) {
      if (!hasRetinaError && urls.retinaUrl) {
        return urls.retinaUrl;
      }
      return urls.highResUrl || urls.scanUrl || PagePlaceholder;
    }

    return urls.highResUrl || urls.scanUrl || PagePlaceholder;
  };

  const getSrcSet = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return undefined;

    const srcSetParts = [`${urls.highResUrl} 1x`];
    if (!hasRetinaError && urls.retinaUrl) {
      srcSetParts.push(`${urls.retinaUrl} 2x`);
    }

    return srcSetParts.length > 0 ? srcSetParts.join(", ") : undefined;
  };

  const imageUrl = getImageUrl();
  const srcSet = getSrcSet();

  const handleImageError = useCallback(() => {
    if (!hasRetinaError) {
      setHasRetinaError(true);
    }
  }, [hasRetinaError]);

  /**
   * Transform mouse coordinates accounting for rotation.
   * At 90°/270° X and Y axes are swapped.
   * At 180°/270° coordinates are inverted.
   */
  const transformCoordinatesForRotation = useCallback(
    (normalizedX: number, normalizedY: number): { x: number; y: number } => {
      const normalizedRotation = ((rotation % 360) + 360) % 360;

      switch (normalizedRotation) {
        case 90:
          return { x: normalizedY, y: 1 - normalizedX };
        case 180:
          return { x: 1 - normalizedX, y: 1 - normalizedY };
        case 270:
          return { x: 1 - normalizedY, y: normalizedX };
        default: // 0
          return { x: normalizedX, y: normalizedY };
      }
    },
    [rotation]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;

      if (isSnipDraggingRef.current) return;

      if (!isZoomActive) return;

      const rect = containerRef.current.getBoundingClientRect();

      // Normalized position (0-1)
      const normalizedX = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );
      const normalizedY = Math.max(
        0,
        Math.min(1, (e.clientY - rect.top) / rect.height)
      );

      // Transform for rotation (used for image which has CSS rotation)
      const transformed = transformCoordinatesForRotation(
        normalizedX,
        normalizedY
      );

      // Convert to offset (-0.5 to 0.5 range, centered)
      setZoomOffset({
        x: (transformed.x - 0.5) * 100,
        y: (transformed.y - 0.5) * 100,
      });

      // Raw offset without rotation transform (used for overlay which handles rotation mathematically)
      setRawZoomOffset({
        x: (normalizedX - 0.5) * 100,
        y: (normalizedY - 0.5) * 100,
      });
    },
    [isZoomActive, transformCoordinatesForRotation]
  );

  const handleMouseEnter = useCallback(() => {
    if (isZoomActive) setIsZooming(true);
  }, [isZoomActive]);

  const handleMouseLeave = useCallback(() => {
    setIsZooming(false);
    setZoomOffset({ x: 0, y: 0 });
    setRawZoomOffset({ x: 0, y: 0 });
  }, []);

  // Zoom scale factor
  const zoomScale = 2;

  // Build transform with rotation + scale + zoom offset
  const getZoomTransform = () => {
    const baseTransform = `rotate(${rotation}deg) scale(${scale})`;

    if (!isZooming || !isZoomActive) {
      return baseTransform;
    }

    // When zooming: apply zoom scale and translate based on mouse position
    return `rotate(${rotation}deg) scale(${scale * zoomScale}) translate(${-zoomOffset.x}%, ${-zoomOffset.y}%)`;
  };

  // Build transform for highlight overlay - only scale, no rotation
  // (rotation is already handled by transformBoundingBoxForRotation in TextHighlightOverlay)
  const getOverlayTransform = () => {
    const baseTransform = `scale(${scale})`;

    if (!isZooming || !isZoomActive) {
      return baseTransform;
    }

    // When zooming: apply zoom scale and translate based on raw mouse position
    // (not rotation-transformed, since overlay handles rotation mathematically)
    return `scale(${scale * zoomScale}) translate(${-rawZoomOffset.x}%, ${-rawZoomOffset.y}%)`;
  };

  return (
    <div className="flex flex-col gap-1 justify-end w-full h-full">
      {opensSection && (
        <TruncatedSectionTitle name={opensSection.sectionName} />
      )}
      <div
        ref={containerRef}
        onClick={() => {
          if (didSnipDragRef.current) return;
          if (snipState && snipState.status !== "idle") return;
          togglePageSelection(pageId);
        }}
        onMouseDown={(e) => {
          didSnipDragRef.current = false;
          if (
            isSelected &&
            !isZoomActive &&
            containerRef.current &&
            onSnipMouseDown
          ) {
            e.preventDefault();
            isSnipDraggingRef.current = true;
            const cRect = containerRef.current.getBoundingClientRect();
            const iRect = imgRef.current?.getBoundingClientRect() ?? cRect;
            onSnipMouseDown(e, cRect, iRect);

            const handleDocMouseMove = (ev: MouseEvent) => {
              if (!isSnipDraggingRef.current || !containerRef.current) return;
              didSnipDragRef.current = true;
              onSnipMouseMove?.(
                ev as unknown as React.MouseEvent,
                containerRef.current.getBoundingClientRect()
              );
            };

            const handleDocMouseUp = () => {
              if (isSnipDraggingRef.current) {
                isSnipDraggingRef.current = false;
                onSnipMouseUp?.();
              }
              document.removeEventListener("mousemove", handleDocMouseMove);
              document.removeEventListener("mouseup", handleDocMouseUp);
              snipCleanupRef.current = null;
            };

            document.addEventListener("mousemove", handleDocMouseMove);
            document.addEventListener("mouseup", handleDocMouseUp);
            snipCleanupRef.current = () => {
              document.removeEventListener("mousemove", handleDocMouseMove);
              document.removeEventListener("mouseup", handleDocMouseUp);
            };
          }
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={() => {}}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "relative flex justify-center items-center overflow-hidden rounded-sm h-full",
          isSelected && !isZoomActive
            ? "cursor-crosshair select-none"
            : "cursor-pointer",
          isSelected && "ring-2 ring-blue-400/70",
          isCurrent &&
            "shadow-[0px_1px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.05),0px_1px_8px_rgba(0,0,0,0.05),0px_1px_16px_rgba(0,0,0,0.05)]"
        )}
      >
        {isFetching ? (
          <div className="w-full h-full bg-muted animate-pulse" />
        ) : (
          <>
            <img
              src={imageUrl}
              srcSet={isZooming ? undefined : srcSet}
              ref={imgRef}
              alt={`Page ${pageId}`}
              loading="lazy"
              className={cn(
                "object-contain transition-transform",
                isZooming ? "duration-0" : "duration-300"
              )}
              style={{
                transform: getZoomTransform(),
                transformOrigin: "center center",
              }}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onError={handleImageError}
            />
            {boundingBoxesData?.matches &&
              boundingBoxesData.matches.length > 0 && (
                <TextHighlightOverlay
                  matches={boundingBoxesData.matches}
                  rotation={rotation}
                  transform={getOverlayTransform()}
                  transformOrigin="center center"
                />
              )}
            {isSelected && snipState && snipState.status !== "idle" && (
              <TextSnipOverlay snipState={snipState} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const DocViewerItem = memo(DocViewerItemComponent);
DocViewerItem.displayName = "DocViewerItem";
